/** 配信 API のクライアント（要件定義 10 章） */

import type { NowResponse, ScheduleResponse } from '@shared/api.ts'

const request = async <T>(path: string): Promise<T> => {
  const response = await fetch(path, { headers: { accept: 'application/json' } })
  if (!response.ok) throw new Error(`${path} が ${response.status} を返しました`)
  return (await response.json()) as T
}

/**
 * 現在の放送。exclude に渡した videoId は放送できないものとして扱われる。
 *
 * 初回だけは index.html が先読みしたレスポンスを使い、
 * JS バンドルの読み込み時間ぶんだけ再生開始を早める。
 */
export const fetchNow = async (excludeVideoIds: string[] = []): Promise<NowResponse> => {
  if (excludeVideoIds.length === 0) {
    const preloaded = await consumePreloadedNow()
    if (preloaded) return preloaded
  }
  const query = excludeVideoIds.length > 0 ? `?exclude=${excludeVideoIds.join(',')}` : ''
  return request<NowResponse>(`/api/now${query}`)
}

const consumePreloadedNow = async (): Promise<NowResponse | null> => {
  const pending = window.__834ch_now
  if (!pending) return null
  // 一度使ったら捨てる。2 回目以降は必ず取り直す
  window.__834ch_now = undefined

  const preloaded = await pending.catch(() => null)
  if (!preloaded || typeof preloaded.data !== 'object' || preloaded.data === null) return null

  const response = preloaded.data as NowResponse
  const serverTime = Date.parse(response.serverTime)
  if (!Number.isFinite(serverTime)) return response

  // 先読みしてから JS が動き出すまでの時間ぶん、サーバー時刻を進めておく
  return {
    ...response,
    serverTime: new Date(serverTime + (Date.now() - preloaded.receivedAt)).toISOString(),
  }
}

export const fetchSchedule = (date?: string): Promise<ScheduleResponse> =>
  request<ScheduleResponse>(date ? `/api/schedule?date=${date}` : '/api/schedule')

/**
 * 再生できなかったことを Worker のログに残す（要件定義 13 章）。
 * 運用者がログを見て blockedVideos に追加できるようにするための導線。
 */
export const reportPlaybackError = async (videoId: string, code: number): Promise<void> => {
  try {
    await fetch('/api/playback-error', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ videoId, code }),
      keepalive: true,
    })
  } catch {
    // 記録できなくても放送は続ける
  }
}
